<template>
  <v-card flat color="transparent" class="bookmark-item--card d-flex flex-column">
    <BulkSelectCheckbox
      v-if="isBulkActionsActive"
      class="checkbox ml-1 mt-1"
      :checked="isSelected"
      @click="toggleSelect"
    />
    <a
      :href="bookmark.brandedUrl" target="_blank" rel="noopener"
      class="d-flex flex-row mb-2"
      :title="bookmark.title"
      @click="onLinkClick"
    >
      <v-col cols="4" class="bookmark-thumbnail-container pa-0">
        <BookmarkThumbnail :bookmark="bookmark" />
      </v-col>
      <v-col cols="8" class="pa-0">
        <div
          class="pr-0 pl-2 py-0 bookmark-headline text-body-1 font-weight-semibold text-neutral900"
        >
          <v-progress-circular
            v-if="bookmark.isLoading && !bookmark.hasSufficientData"
            indeterminate size="16" color="blue400" width="3"
            class="mr-1"
          />
          {{ bookmark.title ?? bookmark.domain }}
        </div>
      </v-col>
    </a>

    <div class="text-neutral600 d-flex align-center mb-3 text-caption">
      <BookmarkSource :bookmark="bookmark" />
      <span class="mx-1">&middot;</span>
      <BookmarkDate :value="bookmark.createdAt" />
    </div>

    <BookmarkTags v-if="bookmark.tags.length" class="mb-3" :tags="bookmark.tags" @tag-selected="onTagSelected" />

    <v-spacer />

    <BookmarkActions
      :key="bookmark.id" :bookmark="bookmark"
      :user-id="userId"
      :public-mode="publicMode"
      :membership="membership"
      class="text-text"
      @favorite="$emit('favorite')"
      @save="$emit('save')"
      @add-to-collection="$emit('add-to-collection')"
      @add-to-collection-from-collection="$emit('add-to-collection-from-collection')"
      @comments="$emit('comments')"
      @like="$emit('like')"
      @likes="$emit('likes')"
      @more="$emit('more')"
      @pin-edit="$emit('pin-edit')"
    />
  </v-card>
</template>

<script setup>
import { computed, defineProps, defineEmits } from 'vue'
import BookmarkSource from './BookmarkSource'
import BookmarkActions from './BookmarkActions'
import BookmarkDate from './BookmarkDate'
import BookmarkTags from './BookmarkTags'
import BookmarkThumbnail from './BookmarkThumbnail'
import { useBulkActionsV2 } from '@/composables/useBulkActionsV2'
import BulkSelectCheckbox from '@/components/BulkSelectCheckbox.vue'


const props = defineProps({
  // Accepts both Bookmark and CollectionItem (when resourceType is 'bookmark')
  bookmark: {
    type: Object,
    required: true,
  },
  userId: {
    type: String,
    default: null,
  },
  membership: {
    type: Object,
    default: null,
  },
  publicMode: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits([
  'toggle-select',
  'tag-selected',
  'favorite',
  'save',
  'add-to-collection',
  'add-to-collection-from-collection',
  'comments',
  'like',
  'likes',
  'more',
  'pin-edit',
])

const { isBulkActionsActive, isItemSelected } = useBulkActionsV2()

const isSelected = computed(() => {
  return isItemSelected(props.bookmark.id)
})

function onLinkClick(event) {
  if (isBulkActionsActive.value) {
    event.preventDefault()
    toggleSelect()
  }
}

function toggleSelect() {
  emit('toggle-select', props.bookmark)
}

function onTagSelected(tag) {
  emit('tag-selected', tag)
}
</script>

<style lang="scss" scoped>
.bookmark-item--card {
  height: 100%;

  .checkbox {
    position: absolute;
    left: 0;
    top: 0;
    z-index: 1;
  }
}

.bookmark-thumbnail-container {
  position: relative;

  .favorite-button {
    position: absolute;
    top: 5px;
    right: 6px;
    background-color: rgba(white, 0.5) !important;
  }
}

.bookmark-headline {
  -webkit-box-orient: vertical !important;
  -webkit-line-clamp: 2 !important;
  line-clamp: 2 !important;
  display: -webkit-box !important;
  overflow: hidden !important;
  word-break: break-word !important;
  line-height: 1.4 !important;
}
</style>
